import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Row, Col, Button, Modal, ModalBody } from "reactstrap";
import { message, Popover, Radio, Space, Tooltip, Modal as AntdModal } from "antd";
import { EditOutlined, InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import QuotationApprovalDrawer from "./QuotationApprovalDrawer";
import AskDocuments from "./AskDocuments";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs from "dayjs";
import costCenterApis from "../../../api/master/costCenter";
import deliveryAddressApis from "../../../api/master/deliveryAddress";
import { SelectOptions, DocumentFormValues } from "./types";
import usersApis from "../../../api/master/users";
import { useDispatch, useSelector } from "react-redux";
import SettingsForm from "./SettingsForm";
import contractorsApis from "../../../api/master/contractors";
import sequenceApis from "../../../api/master/sequence";
import brandMasterApis from "../../../api/master/brandMaster";
import rolesApi from "../../../api/master/roles";
interface SequenceType {
  module: string;
  level: string;
  siteIds: string[];
}
const RFQGeneralDetails: React.FC = (props: any) => {
  const [sequenceAccess, setSequenceAccess] = useState<SequenceType>();

  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [restartFiscalYear, setRestartFiscalYear] = useState(false);

  const dispatch: any = useDispatch();
  const {
    handleSubmit,
    watch,
    setValue,
    control,
    getValues,
    reset,
    formState: { errors },
  } = useFormContext();

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    []
  );
  const [costCenterOptions, setCostCenterOptions] = useState<SelectOptions[]>(
    []
  );
  const AllFormDetails = watch();
  const siteIdWatch = watch("siteId");
  const approvalRequired = watch("approvalRequired");
  const supplierDocsRequired = watch("supplierDocsRequired");
  const needByDateType = watch("needByDateType");
  const paymentTermsWatch = watch("paymentTerms");
  const isSealedBidWatch = watch("isSealedBid");
  const isStrictEvaluationTimerWatch = watch("isStrictEvaluationTimer");
  const deliveryAddressId = watch("deliveryAddressId");
  const billingAddressId = watch("billingAddressId");
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  const [settings, setSettings] = useState({
    open: false,
    data: {
      needByDateType: AllFormDetails.needByDateType || "ORDER_LEVEL",
      deliveryAddressType: AllFormDetails.deliveryAddressType || "ORDER_LEVEL",
      autoRelease: "Y",
      additionalCharges: "Flexible",
      deadlineType: "SOFT",
      pricingMode: AllFormDetails.pricingMode || "Base Price",
      isSealedBid: AllFormDetails.isSealedBid || "N",
      isStrictEvaluationTimer: AllFormDetails.isStrictEvaluationTimer || "N",
    },
  });
  const [documentData, setDocumentData] = useState<any>();
  const [documentsPopoverVisible, setDocumentsPopoverVisible] = useState(true);
  const [isAskDocumentsDrawerOpen, setIsAskDocumentsDrawerOpen] =
    useState(false);
  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [brandOptions, setBrandOptions] = useState<SelectOptions[]>([]);
  const [conversionRemarksModal, setConversionRemarksModal] = useState(false);
  const [conversionRemarks, setConversionRemarks] = useState("");

  // Initialize conversion remarks from form data
  useEffect(() => {
    const formConversionRemarks = watch("conversionRemarks");
    if (formConversionRemarks && !conversionRemarks) {
      setConversionRemarks(formConversionRemarks);
    }
  }, [watch, conversionRemarks]);

  // Track previous approval required value to detect actual changes
  const [prevApprovalRequired, setPrevApprovalRequired] =
    useState(approvalRequired);

  // Update previous value when approval required changes
  useEffect(() => {
    setPrevApprovalRequired(approvalRequired);
  }, [approvalRequired]);

  // Clear conversion remarks only when user changes from N to Y
  useEffect(() => {
    if (prevApprovalRequired === "N" && approvalRequired === "Y") {
      setConversionRemarks("");
      setValue("conversionRemarks", "");
    }
  }, [approvalRequired, setValue, prevApprovalRequired]);

  const submitForm = async (data: any) => {
    console.log("documentData", documentData);
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval);
    setValue("numberingMode", numberingMode);
    // Convert preferredBrands from array of IDs to array of objects
    const preferredBrandsValue = watch("preferredBrands");
    if (Array.isArray(preferredBrandsValue) && preferredBrandsValue.length > 0) {
      // Convert array of brand IDs to array of objects with brandId and brandName
      const selectedBrands = preferredBrandsValue.map((brandId: string) => {
        const brand = brandOptions.find((opt) => opt.value === brandId);
        return {
          brandId: brandId,
          brandName: brand?.label || "",
        };
      });
      setValue("preferredBrands", selectedBrands);
    } else {
      setValue("preferredBrands", []);
    }

    if (approvalRequired == "Y" && approvals?.data?.length === 0) {
      return;
    }
    if (
      approvalRequired == "N" &&
      AllFormDetails?.prId &&
      (!conversionRemarks || conversionRemarks.trim() === "")
    ) {
      return;
    }
    if (supplierDocsRequired == "y" && documentData?.documents.length === 0) {
      return;
    } else {
      setValue("documents", documentData);
    }

    // Detect if the logged-in user is the final approver for the Quotation
    // module so downstream qty validation can bypass the block for admin/final approvers.
    if (siteIdWatch) {
      try {
        const moduleRes = await rolesApi.findAllModuleConfig();
        const moduleID = moduleRes?.data?.find(
          (x: any) => x.moduleName == "Quotation",
        )?.moduleID;
        let isFinal = false;
        if (moduleID) {
          const res = await rolesApi.fetchHierarchyUsersByModuleId(
            moduleID,
            siteIdWatch,
          );
          const msg = (res?.message || "").toString().toLowerCase();
          isFinal = msg.includes("you are the final");
        }
        setValue("isFinalApprover", isFinal);
      } catch (err) {
        console.log("Error checking final approver:", err);
        setValue("isFinalApprover", false);
      }
    } else {
      setValue("isFinalApprover", false);
    }

    props?.next();
  };

  const fetchDeliveryAddress = async () => {
    try {
      const { data, status } = await deliveryAddressApis.fetchDeliveryAddress();
      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDeliveryAddressBySite = async (siteId: any) => {
    try {
      const { data, status } = await deliveryAddressApis.fetchAddressBySite(
        siteId
      );

      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log("Error fetching delivery addresses by site:", error);
    }
  };

  const handleDocumentsSubmit = (data: DocumentFormValues) => {
    setDocumentData(data);
    setDocumentsPopoverVisible(false);
    onAskDocumentsClose();
  };

  const showAskDocumentsDrawer = () => {
    setIsAskDocumentsDrawerOpen(true);
  };

  const onAskDocumentsClose = () => {
    setIsAskDocumentsDrawerOpen(false);
  };

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContractor = async () => {
    try {
      const data = await contractorsApis.fetchContractorBySiteId({
        id: siteIdWatch.toString(),
      });
      if (data) {
        let contractorOptions = data.data?.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCostCenter = async () => {
    try {
      const { data, status } = await costCenterApis.fetchCostCenter();
      if (status) {
        const costCenterOptions = data.map((item: any) => ({
          label: item.costCenterName,
          value: item.id.toString(),
        }));
        setCostCenterOptions(costCenterOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response: any = await brandMasterApis.fetchBrandDropDown();
      const {
        data: brandsList,
        status,
      }: {
        status: boolean;
        data: { name: string; id: number }[];
      } = response || {};
      if (status) {
        const brandOptions = brandsList.map((brand: any) => ({
          label: brand.name,
          value: brand.id.toString(),
        }));
        setBrandOptions(brandOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }

    let address = options?.find((item: any) => item[matchKey] == id);

    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city},{address?.districtName}, {address?.stateName},
              {address?.countryName} - {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };

  useEffect(() => {
    if (siteIdWatch) {
      fetchContractor();
      fetchDeliveryAddressBySite(siteIdWatch);
      if (AllFormDetails?.prId) return;
      // Only clear deliveryAddressId if it doesn't already have a valid value
      // This prevents clearing when user comes back from another step
      const currentDeliveryAddressId = watch("deliveryAddressId");
      if (
        !currentDeliveryAddressId ||
        currentDeliveryAddressId === " " ||
        currentDeliveryAddressId === ""
      ) {
        setValue("deliveryAddressId", " ");
      }
    }
  }, [siteIdWatch]);

  useEffect(() => {
    fetchSites();

    fetchCostCenter();
    fetchDeliveryAddress();
    fetchBrands();

    const {
      approvalsData,
      deliveryAddressType,
      needByDateType,
      autoRelease,
      quotationFor,
      supplierDocsRequired,
      documents,
    } = AllFormDetails;
    console.log("AllFormDetails", JSON.stringify(AllFormDetails));

    if (documents) {
      setDocumentData(documents);
    }
    if (approvalsData) {
      setApprovals(JSON.parse(approvalsData));
    }
    if (autoRelease) {
      setValue("autoRelease", autoRelease);
    } else {
      setValue("autoRelease", "Y");
    }
    if (needByDateType) {
      setValue("needByDateType", needByDateType);
    } else {
      setValue("needByDateType", "ORDER_LEVEL");
    }
    if (deliveryAddressType) {
      setValue("deliveryAddressType", deliveryAddressType);
    } else {
      setValue("deliveryAddressType", "ORDER_LEVEL");
    }
    if (supplierDocsRequired === "Y") {
      setDocumentsPopoverVisible(false);
    }

    if (quotationFor) {
      setValue("quotationFor", quotationFor);
    } else {
      setValue("quotationFor", "Material");
    }

  }, []);

  // Handle preferredBrands initialization when brands are loaded or form data changes
  useEffect(() => {
    if (brandOptions.length === 0) return;

    const preferredBrandsValue = watch("preferredBrands");
    if (preferredBrandsValue) {
      if (Array.isArray(preferredBrandsValue)) {
        // If it's already an array of objects, extract IDs for the multi-select
        if (preferredBrandsValue.length > 0 && typeof preferredBrandsValue[0] === "object" && preferredBrandsValue[0].brandId) {
          const brandIds = preferredBrandsValue.map((brand: any) => brand.brandId?.toString() || brand.id?.toString());
          setValue("preferredBrands", brandIds);
        }
        // If it's already an array of IDs, it's already in the correct format, no need to change
      } else if (typeof preferredBrandsValue === "string" && preferredBrandsValue.trim() !== "") {
        // If it's a string (old format), set empty array as we're moving to new format
        setValue("preferredBrands", []);
      }
    }
  }, [brandOptions, watch, setValue]);

  // useEffect(() => {
  //   fetchSequenceAccess();
  // }, []);

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Quotation" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);

      // Check if mode is already persisted in form (e.g. user navigated back from next step)
      const savedMode = getValues("numberingMode");
      if (savedMode) {
        setNumberingMode(savedMode);
        return;
      }

      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0) {
        // Check if current site has a sequence
        const siteHasSequence = _sequenceAccess.some(
          (seq: any) => (seq.siteId?.toString() === siteIdWatch?.toString() || seq.level == 'Org')
        );
        setNumberingMode(siteHasSequence ? 'auto' : 'manual');
      } else {
        setNumberingMode('manual');
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode('manual');
    }
  };

  useEffect(() => {
    if (siteIdWatch) {
      fetchSequenceAccess();
    }
  }, [siteIdWatch]);

  const getCurrentSiteSequence = (): any | null => {
    if (!siteIdWatch || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === siteIdWatch?.toString()
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find(
      (seq) => seq.level === "Org"
    );

    if (orgSequence) return orgSequence;

    return null;
  };


  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!currentSiteSequence;

  // Auto-generate number when in auto mode
  useEffect(() => {
    const existingValue = getValues("rfqId");

    if (numberingMode === 'auto' && currentSiteSequence && !existingValue) {
      const { prefix } = currentSiteSequence;
      const rfqNumber = `${prefix}`;
      setValue('rfqId', rfqNumber);
    }
  }, [numberingMode, currentSiteSequence]);

  const prevSiteRef = useRef(siteIdWatch);

  // Reset number and set mode when site changes (not on initial mount)
  useEffect(() => {
    const prevSite = prevSiteRef.current;

    // Only reset when site actually changed, not on initial mount
    if (prevSite && prevSite !== siteIdWatch) {
      setValue("purchaseRequestId", "");
      setValue("rfqId", "");
      // Clear persisted mode so new site gets fresh evaluation
      setValue("numberingMode", "");
      setNumberingMode("manual");
    }

    // Respect persisted mode on remount
    const savedMode = getValues("numberingMode");
    if (savedMode) {
      setNumberingMode(savedMode);
    } else if (!siteIdWatch) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }

    prevSiteRef.current = siteIdWatch;
  }, [siteIdWatch]);

  useEffect(() => { }, [dispatch]);

  const openSettingsForm = () => {
    setSettings((prev) => ({
      ...prev,
      open: true,
    }));
  };

  useEffect(() => {
    setValue("needByDateType", AllFormDetails.needByDateType || "ORDER_LEVEL");
    setValue(
      "deliveryAddressType",
      AllFormDetails.deliveryAddressType || "ORDER_LEVEL"
    );
    setValue("autoRelease", "Y");
    setValue("additionalCharges", "Flexible");
    setValue("deadlineType", "SOFT");
    setValue("pricingMode", AllFormDetails.pricingMode || "Base Price");
    setValue("isSealedBid", AllFormDetails.isSealedBid || "N");
    setValue("isStrictEvaluationTimer", AllFormDetails.isStrictEvaluationTimer || "N");
  }, []);

  const handleSettingsSubmit = (updatedSettings: any) => {
    setSettings({
      open: false,
      data: updatedSettings,
    });
    setValue("needByDateType", updatedSettings.needByDateType);
    setValue("deliveryAddressType", updatedSettings.deliveryAddressType);
    setValue("autoRelease", updatedSettings.autoRelease);
    setValue("additionalCharges", updatedSettings.additionalCharges);
    setValue("deadlineType", updatedSettings.deadlineType);
    setValue("pricingMode", updatedSettings.pricingMode || "Base Price");
    setValue("isSealedBid", updatedSettings.isSealedBid || "N");
    setValue("isStrictEvaluationTimer", updatedSettings.isStrictEvaluationTimer || "N");
  };

  return (
    <>
      <Row>
        <div
          style={{ justifyContent: "end", display: "flex", marginTop: "-2%" }}
        >
          <Button color="neutral" onClick={openSettingsForm}>
            Settings &nbsp; <SettingOutlined />
          </Button>
        </div>
      </Row>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Row className="gy-4">
          <Col lg="4">
            <FormSelect
              name="siteId"
              label="Site Name"
              options={sitesOptions}
              required={true}
              placeholder="Select"
              disabled={AllFormDetails?.prId ? true : false}
            />
          </Col>
          {/* {(sequenceAccess?.level == "Site" &&
            !sequenceAccess?.siteIds?.includes(siteIdWatch)) ||
            sequenceAccess?.module == "" ? (
            <Col lg="4">
              <FormInput
                name="rfqId"
                label={`Quotation Number`}
                required={true}
                placeholder="Enter Quotation Number"
              />
            </Col>
          ) : (
            <></>
          )} */}

          <Col lg="4">
            <div style={{ position: 'relative' }}>
              <FormInput
                name="rfqId"
                label="Quotation Number"
                required={numberingMode === 'manual'}
                disabled={numberingMode === 'auto' || AllFormDetails?.type === "draft"}
                placeholder={numberingMode === 'auto' ? 'Auto-generated' : 'Enter quotation Number'}
              />
              <Tooltip title="Click here to enable or disable auto-generation of quotation numbers.">
                <SettingOutlined
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50px',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#1890ff',
                    zIndex: 1,
                  }}
                  onClick={() => {
                    setTempNumberingMode(numberingMode);
                    setShowNumberingModal(true);
                  }}
                />
              </Tooltip>
            </div>
          </Col>

          <Col lg="4">
            <FormSelect
              name="quotationFor"
              label="Quotation For"
              defaultValue={{
                label: "Material",
                value: "Material",
              }}
              options={[
                { label: "Material", value: "Material" },
                { label: "Service", value: "Service" },
              ]}
              required={true}
              placeholder="Select Quotation Type"
            />
          </Col>
          {/* <Col lg="4">
            <FormMultiSelect
              name="preferredBrands"
              label="Preferred Brands / Make"
              options={brandOptions}
              placeholder="Select Preferred Brands / Make"
            />
          </Col> */}
          <Col lg="8">
            <FormInput
              name="quotationDescription"
              label="Quotation Description"
              required={true}
              type="text"
              minLength={2}
              maxLength={100}
              placeholder="Enter Description"
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="costCenter"
              label="Cost Center"
              options={costCenterOptions}
              required={false}
              placeholder="Select Cost Center"
            />
          </Col>
          <Col lg="4">
            <FormDatePicker
              name="lastSubmissionDate"
              label="Last Submission Date & Time"
              required={true}
              placeholder="Select Date & Time"
              minDate={new Date()}
              showTime={true}
              dateFormat="DD-MMM-YYYY hh:mm A"
            />
          </Col>
          {/* <Col lg="4">
            <FormSelect
              name="deadlineType"
              label="Submission Type"
              options={[
                { label: "Soft", value: "SOFT" },
                { label: "Hard", value: "HARD" },
              ]}
              required={true}
              placeholder="Select"
            />
          </Col> */}

          <Col lg="4">
            <FormSelect
              name="contractorId"
              label="Contractor"
              required={true}
              options={contractorOptions}
              placeholder="Select"
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="approvalRequired"
              label="Approval Required ?"
              disabled={!siteIdWatch}
              options={[
                { label: "Yes", value: "Y" },
                { label: "No", value: "N" },
              ]}
              placeholder="Select"
              required={true}
            />
            <div style={{ display: "none" }}>
              <FormInput name="approvalsData" label="" type="text" noMaxLength />
            </div>
            {approvalRequired == "Y" && (
              <Popover
                content="Click here to set required approvals"
                title="Approval Required"
              >
                <a
                  href="#"
                  className="success"
                  onClick={(e) => {
                    e.preventDefault();
                    setApprovals((pre) => {
                      return {
                        ...pre,
                        open: true,
                      };
                    });
                  }}
                >
                  <EditOutlined /> Set Approvals &nbsp;
                  {approvals?.data?.length == 0 ? (
                    <span className="mandatory">Please set approvals</span>
                  ) : (
                    <></>
                  )}
                </a>
              </Popover>
            )}
            {approvalRequired == "N" && AllFormDetails?.prId && (
              <div>
                <div
                  style={{
                    marginTop: "5px",
                    color: "#2263b3",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                  onClick={() => setConversionRemarksModal(true)}
                >
                  <EditOutlined /> Set Remarks &nbsp;
                  {!conversionRemarks && (
                    <span className="mandatory">Please set remarks</span>
                  )}
                </div>
              </div>
            )}
          </Col>

          <Col lg="4">
            <FormSelect
              name="supplierDocsRequired"
              label="Require Documents from supplier?"
              options={[
                { label: "Yes", value: "Y" },
                { label: "No", value: "N" },
              ]}
              required={true}
              placeholder="Select"
            />
            {watch("supplierDocsRequired") == "Y" && (
              <Popover
                content="Click here to ask required documents"
                title="Documents Required"
              >
                <a
                  href="#"
                  className="success"
                  onClick={(e) => {
                    e.preventDefault();
                    showAskDocumentsDrawer();
                  }}
                >
                  <EditOutlined /> Ask Required Documents &nbsp;
                  {documentsPopoverVisible ? (
                    <span className="mandatory">Please set documents</span>
                  ) : (
                    <></>
                  )}
                </a>
              </Popover>
            )}
          </Col>

          <Col lg="4">
            <FormSelect
              name="billingAddressId"
              label={`Billing Address${deliveryAddressOptions.length == 0
                ? "( Empty Billing Address List)"
                : ""
                }`}
              options={deliveryAddressOptions}
              placeholder={siteIdWatch ? "-Select-" : "Select a site first"}
              disabled={!siteIdWatch}
              popOverOptions={
                <ShowAddressPopOver
                  id={billingAddressId}
                  matchKey="id"
                  options={deliveryAddressList}
                  text="View Selected Billing Address"
                />
              }
              required={true}
              tooltipText="Select the billing address for the Quotation."
            />
          </Col>
          {watch("deliveryAddressType") === "ORDER_LEVEL" && (
            <Col lg="4">
              <FormSelect
                name="deliveryAddressId"
                label="Delivery Address"
                options={deliveryAddressOptions}
                required={true}
                placeholder={siteIdWatch ? "-Select-" : "Select a site first"}
                tooltipText="Select the delivery address for the Quotation."
                disabled={AllFormDetails?.prId ? true : false}
                popOverOptions={
                  <ShowAddressPopOver
                    id={deliveryAddressId}
                    matchKey="id"
                    options={deliveryAddressList}
                    text="View Selected Delivery Address"
                  />
                }
                dropdownRender={(menu) => <>{menu}</>}
              />
            </Col>
          )}

          {needByDateType === "ORDER_LEVEL" && (
            <Col lg="4">
              <FormDatePicker
                name="needByDate"
                label="Need By Date"
                required={true}
                placeholder="Select Date"
                minDate={new Date()}
              />
            </Col>
          )}

          <Col lg="4">
            <FormSelect
              name="paymentTerms"
              label="Payment Terms"
              options={[
                { label: "Cash & Carry", value: "Cash and Carry" },
                // { label: "Advance", value: "Advance" },
                { label: "Credit", value: "Credit" },
              ]}
              required={true}
              placeholder="Select Payment Terms"
              tooltipText="Select the payment terms for the Quotation."
            />
          </Col>
          {paymentTermsWatch == "Credit" && (
            <Col lg="4">
              <FormInput
                name="creditDays"
                type="number"
                label="Credit Days"
                required={true}
                placeholder="Enter Credit Days"
              />
            </Col>
          )}
          {isSealedBidWatch === "Y" && (
            <>
              <Col lg="4">
                <FormDatePicker
                  name="sealedBidSubmissionDate"
                  label="Sealed Bid Submission Date & Time"
                  required={true}
                  placeholder="Select Date & Time"
                  showTime={true}
                  dateFormat="DD-MMM-YYYY hh:mm A"
                  disabledDate={(current: any) => {
                    if (!current) return false;
                    const lastSubDate = getValues("lastSubmissionDate");
                    if (lastSubDate) {
                      return current.isBefore(dayjs(lastSubDate), "day");
                    }
                    return current.isBefore(dayjs(), "day");
                  }}
                  disabledTime={(current: any) => {
                    const lastSubDate = getValues("lastSubmissionDate");
                    if (!lastSubDate || !current) return {};
                    const ref = dayjs(lastSubDate);
                    if (!current.isSame(ref, "day")) return {};
                    const refHour = ref.hour();
                    const refMinute = ref.minute();
                    return {
                      disabledHours: () =>
                        Array.from({ length: 24 }, (_, i) => i).filter(
                          (h) => h < refHour
                        ),
                      disabledMinutes: (selectedHour: number) => {
                        if (selectedHour < refHour) return Array.from({ length: 60 }, (_, i) => i);
                        if (selectedHour === refHour)
                          return Array.from({ length: 60 }, (_, i) => i).filter(
                            (m) => m < refMinute
                          );
                        return [];
                      },
                    };
                  }}
                  customValidation={{
                    validate: (value: any) => {
                      if (!value) return true;
                      const lastSubDate = getValues("lastSubmissionDate");
                      if (lastSubDate && dayjs(value).isBefore(dayjs(lastSubDate))) {
                        return "Must be on or after Last Submission Date & Time";
                      }
                      return true;
                    },
                  }}
                />
              </Col>
              <Col lg="4">
                <FormInput
                  name="postalAddress"
                  label="Postal Address"
                  required={true}
                  type="text"
                  placeholder="Enter Postal Address"
                />
              </Col>
            </>
          )}
          {isStrictEvaluationTimerWatch === "Y" && (
            <Col lg="4">
              <FormDatePicker
                name="evaluationDate"
                label="Evaluation Date & Time"
                required={true}
                placeholder="Select Date & Time"
                showTime={true}
                dateFormat="DD-MMM-YYYY hh:mm A"
                disabledDate={(current: any) => {
                  if (!current) return false;
                  const sealedBidDate = getValues("sealedBidSubmissionDate");
                  if (sealedBidDate) {
                    return current.isBefore(dayjs(sealedBidDate), "day");
                  }
                  return current.isBefore(dayjs(), "day");
                }}
                disabledTime={(current: any) => {
                  const sealedBidDate = getValues("sealedBidSubmissionDate");
                  if (!sealedBidDate || !current) return {};
                  const ref = dayjs(sealedBidDate);
                  if (!current.isSame(ref, "day")) return {};
                  const refHour = ref.hour();
                  const refMinute = ref.minute();
                  return {
                    disabledHours: () =>
                      Array.from({ length: 24 }, (_, i) => i).filter(
                        (h) => h < refHour
                      ),
                    disabledMinutes: (selectedHour: number) => {
                      if (selectedHour < refHour) return Array.from({ length: 60 }, (_, i) => i);
                      if (selectedHour === refHour)
                        return Array.from({ length: 60 }, (_, i) => i).filter(
                          (m) => m < refMinute
                        );
                      return [];
                    },
                  };
                }}
                customValidation={{
                  validate: (value: any) => {
                    if (!value) return true;
                    const sealedBidDate = getValues("sealedBidSubmissionDate");
                    if (sealedBidDate && dayjs(value).isBefore(dayjs(sealedBidDate))) {
                      return "Must be on or after Sealed Bid Submission Date & Time";
                    }
                    return true;
                  },
                }}
              />
            </Col>
          )}
        </Row>

        <Row className="gy-4 mt-1">
          <Col>
            <Button color="primary" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
      {settings.open && (
        <SettingsForm
          open={settings.open}
          onClose={() => {
            setSettings((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          prId={AllFormDetails?.prId}
          initialValues={settings.data}
          onSubmit={handleSettingsSubmit}
        />
      )}

      {approvals.open && (
        <QuotationApprovalDrawer
          open={approvals.open}
          siteId={siteIdWatch}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}
      {isAskDocumentsDrawerOpen && (
        <AskDocuments
          open={isAskDocumentsDrawerOpen}
          onClose={onAskDocumentsClose}
          onSubmit={handleDocumentsSubmit}
          documentData={
            documentData || {
              documents: [
                { documentName: "", description: "", mandatory: false },
              ],
            }
          }
        />
      )}

      {/* Conversion Remarks Modal */}
      <Modal
        isOpen={conversionRemarksModal}
        toggle={() => setConversionRemarksModal(false)}
        className="modal-dialog-centered"
        size="md"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setConversionRemarksModal(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>
          <h5 className="title">Remarks</h5>
          <div style={{ marginTop: "15px" }}>
            <div className="form-group">
              <div className="form-control-wrap">
                <textarea
                  id="conversionRemarksInput"
                  placeholder="Enter reason for proceeding without approval"
                  className="form-control"
                  value={conversionRemarks}
                  onChange={(e: any) => setConversionRemarks(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Button
              color="primary"
              disabled={!conversionRemarks.trim()}
              onClick={() => {
                setValue("conversionRemarks", conversionRemarks);
                setConversionRemarksModal(false);
              }}
              className="me-2"
            >
              Save
            </Button>
            <Button
              color="secondary"
              onClick={() => setConversionRemarksModal(false)}
            >
              Cancel
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Quotation Number Configuration Modal */}
      <AntdModal
        title="Configure Quotation Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error("Auto-generation is not configured for this site.");
                  return;
                }

                setNumberingMode(tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix } = currentSiteSequence;
                  const rfqNumber = `${prefix}`;
                  setValue("rfqId", rfqNumber);
                }
                else if (tempNumberingMode === "manual") {
                  setValue("rfqId", "");
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your quotation numbers are currently set to auto-generate to save time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!siteIdWatch && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined /> Sequence configuration is not set for this site. Please configure the sequence.
          </div>
        )}

        {siteIdWatch && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined />{" "}
            {activeSequences.length === 0
              ? "No sequences configured for Quotations. Please set up a sequence first."
              : "No sequence configured for the selected site. Please set a sequence for this site."}
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating quotation numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>
                Enter quotation numbers manually
              </span>
            </Radio>
          </Space>
        </Radio.Group>
      </AntdModal>
    </>
  );
};

export default React.memo(RFQGeneralDetails);
